/**
 * Competition vocabularies — the closed word lists the competition model is
 * built from (sports → disciplines → events → phases → meets → results → medals).
 *
 * ONE list per vocabulary, and it lives here. The database reads these tuples
 * (a Postgres enum or a CHECK constraint is generated from each — see
 * `apps/api/src/db/schema/_custom-types.ts`), the API's zod schemas read them,
 * and the web reads them. A second hand-typed copy on either side of the wire
 * is how `auth.schema` once lost ORGANIZATION (CLAUDE.md, "Bugs already made
 * here" #4).
 *
 * Why lists at all: the predecessor kept every one of these as free text.
 * `meet_stage` had 123 spellings (FINAL 307 rows, FINALS 275), "winner" had
 * six, and one `results.status` column mixed workflow, outcome, qualification
 * and DNS / DSQ marks. Each list below is one of the things that column was
 * trying to say.
 *
 * Two kinds, and the difference is how they change:
 *
 *  · CLOSED sets are real Postgres enums. They describe the model itself
 *    (a medal is gold, silver or bronze) and are not expected to grow.
 *
 *  · GROWING sets are `text` + a CHECK built from the tuple. A new phase type
 *    or a new invalid-result mark WILL turn up once real sports are loaded,
 *    and a CHECK can be dropped and re-added in one transaction — a value
 *    added with `ALTER TYPE … ADD VALUE` cannot be used in the transaction
 *    that adds it, and Drizzle runs all pending migrations in one.
 *
 * Either way, changing a list is a migration: edit the tuple, run
 * `pnpm db:generate`, review the SQL. Nothing here takes effect on its own.
 *
 * Every value is UPPER_SNAKE (`^[A-Z0-9_]+$`) — they are written into DDL as
 * literals. `apps/api/tests/unit/competition-vocab.test.ts` pins that.
 */

// ─── Closed sets (Postgres enums) ────────────────────────────────────────────

/** Who may enter an event. OPEN = no gender restriction (equestrian, some sailing classes). */
export const EVENT_GENDERS = ["MEN", "WOMEN", "MIXED", "OPEN"] as const;
export type EventGender = (typeof EVENT_GENDERS)[number];

/**
 * What one entry in an event is. TEAM covers everything with a roster: a pair,
 * a relay, a crew, a football squad. It is a property of the EVENT — the
 * predecessor used "discipline" for Singles / Doubles / Team.
 */
export const COMPETITOR_TYPES = ["INDIVIDUAL", "TEAM"] as const;
export type CompetitorType = (typeof COMPETITOR_TYPES)[number];

export const MEDALS = ["GOLD", "SILVER", "BRONZE"] as const;
export type Medal = (typeof MEDALS)[number];

/** How a head-to-head meet ended for one competitor. BYE = advanced without playing. */
export const MEET_OUTCOMES = ["WIN", "LOSS", "DRAW", "BYE"] as const;
export type MeetOutcome = (typeof MEET_OUTCOMES)[number];

/**
 * Who wrote a slot's result (`meet_competitors.result_source`, migration
 * 0029). Set by the save statement itself, never sent by a client:
 *
 *   TIMING    the timing feed (`POST /meets/:id/timing`) wrote it last
 *   MANUAL    an operator typed it, and the feed never had it
 *   OVERRIDE  an operator changed what the feed wrote — the feed keeps off it
 *
 * NULL = no result yet. A rank-only update (a row re-ranked because another
 * row moved) keeps the source as it is.
 */
export const RESULT_SOURCES = ["TIMING", "MANUAL", "OVERRIDE"] as const;
export type ResultSource = (typeof RESULT_SOURCES)[number];

/**
 * WHEN a meet happens — and nothing else. Separate from `RESULT_STATUSES`
 * because one status could not say "delayed, start list published" or
 * "abandoned at 60 minutes with a score on the board".
 */
export const SCHEDULE_STATUSES = ["TENTATIVE", "SCHEDULED", "DELAYED", "INTERRUPTED", "POSTPONED", "CANCELLED"] as const;
export type ScheduleStatus = (typeof SCHEDULE_STATUSES)[number];

/**
 * How far a meet's RESULT has got.
 *
 * THE ORDER IS PART OF THE CONTRACT. This becomes a Postgres enum, enums
 * compare in declaration order, and queries ask `result_status >= 'UNOFFICIAL'`
 * ("has a result worth showing"). Insert a value in the wrong place and those
 * comparisons change meaning without a single query changing.
 */
export const RESULT_STATUSES = ["NONE", "START_LIST", "LIVE", "UNOFFICIAL", "OFFICIAL"] as const;
export type ResultStatus = (typeof RESULT_STATUSES)[number];

/** A meet status as an audit headline prints it, without underscores: `START_LIST` → `STARTLIST`. */
export function meetStatusWord(status: ResultStatus | ScheduleStatus): string {
	return status.replace(/_/g, "");
}

/**
 * The same tidy on a stored headline: audit rows are append-only, so rows
 * written before `meetStatusWord` still say `START_LIST` and are fixed on read.
 */
export function tidyMeetReason(reason: string): string {
	let out = reason;
	for (const s of [...RESULT_STATUSES, ...SCHEDULE_STATUSES]) {
		if (s.includes("_")) out = out.replace(new RegExp(`\\b${s}\\b`, "g"), meetStatusWord(s));
	}
	return out;
}

/** Which side of a source meet fills a bracket slot: "winner of QF1", "loser of SF2". */
export const SLOT_SOURCE_OUTCOMES = ["WINNER", "LOSER"] as const;
export type SlotSourceOutcome = (typeof SLOT_SOURCE_OUTCOMES)[number];

// ─── Growing sets (text + CHECK) ─────────────────────────────────────────────

/**
 * What the headline number `result_value` measures. One canonical unit each:
 * TIME in milliseconds, DISTANCE in millimetres, WEIGHT in kilograms, POINTS
 * and SCORE as counted. NONE = the meet is decided by outcome alone.
 */
export const RESULT_TYPES = ["TIME", "DISTANCE", "POINTS", "SCORE", "WEIGHT", "NONE"] as const;
export type ResultType = (typeof RESULT_TYPES)[number];

/**
 * The SHAPE of a result's sport-specific `detail`, and so which scoring screen
 * and which zod schema apply (`./results`):
 *
 *   TIMED    swimming, track, cycling, rowing — reaction time, splits
 *   ATTEMPTS jumps, throws — attempts with mark, wind, valid / foul
 *   SETS     racket, net and period sports — per-period scores, tie-breaks
 *   BOUT     combat sports — rounds, penalties, judges' cards, win method
 *   JUDGED   gymnastics, diving, taolu — parts with component scores
 *   ROUNDS   golf, bowling, sailing, shooting series — per-round value, discards
 *   LIFTS    weightlifting, powerlifting — attempts with weight, good / no-lift
 *   GENERIC  anything not modelled yet, so no sport is blocked on day one
 */
export const RESULT_FAMILIES = ["TIMED", "ATTEMPTS", "SETS", "BOUT", "JUDGED", "ROUNDS", "LIFTS", "GENERIC"] as const;
export type ResultFamily = (typeof RESULT_FAMILIES)[number];

/**
 * What kind of stage a phase is. The label a scoreboard shows is the phase's
 * own `name`; this is the machine-readable kind behind it.
 */
export const PHASE_TYPES = [
	"QUALIFICATION",
	"HEAT",
	"GROUP",
	"ROUND",
	"R128",
	"R64",
	"R32",
	"R16",
	"QUARTERFINAL",
	"SEMIFINAL",
	"REPECHAGE",
	"BRONZE",
	"FINAL",
	"RANKING",
	"TIEBREAK",
] as const;
export type PhaseType = (typeof PHASE_TYPES)[number];

/**
 * How a phase ranks its competitors ACROSS its meets — what replaces the
 * predecessor's copied "Heat Summary" meet:
 *
 *   NONE        no table (a knock-out round: each meet stands alone)
 *   MEET_RANK   the rank in the phase's single meet (a straight final)
 *   RESULT_BEST best `result_value` over the phase's meets (heats)
 *   RESULT_SUM  sum of `result_value` (multi-round totals — golf)
 *   POINTS_SUM  sum of `points` (group tables, combined events)
 */
export const STANDING_RULES = ["NONE", "MEET_RANK", "RESULT_BEST", "RESULT_SUM", "POINTS_SUM"] as const;
export type StandingRule = (typeof STANDING_RULES)[number];

/** Where an ENTRY stands in the event as a whole (not in one meet — that is `IRM_CODES`). */
export const COMPETITOR_STATUSES = ["ENTERED", "WITHDRAWN", "DISQUALIFIED"] as const;
export type CompetitorStatus = (typeof COMPETITOR_STATUSES)[number];

/** A roster member's part. GUIDE, PILOT and COX compete alongside the athlete and share the medal. */
export const MEMBER_ROLES = ["ATHLETE", "RESERVE", "GUIDE", "PILOT", "COX"] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

/**
 * Invalid Result Marks — why a competitor has no valid result in a meet. An
 * empty `irm` means the result IS valid; the predecessor wrote these into the
 * same column as the workflow status.
 *
 *   DNS did not start        DNF did not finish       DSQ disqualified
 *   NM  no mark              WD  withdrawn            WO  walkover
 *   RET retired              DNC did not come to the starting area
 *   OCS on course side at the start                   BFD black-flag disqualification
 *   UFD U-flag disqualification                       DNE disqualification, not excludable
 *   RDG redress given
 *
 * The ROUNDS / TIMED sports' own marks (phase 5). On a SLOT each one needs
 * the CHECK `ck_meet_competitors_irm` widened (a migration); a ROUND's `irm`
 * lives in jsonb and needs none.
 *   DPI discretionary penalty imposed        SCP scoring penalty applied
 *   ZFP 20 % scoring penalty                 STP standard penalty
 *   DCT disqualified after a collision (team / match racing instructions)
 *   NSC did not sail the course              (sailing, World Sailing RRS A11)
 *   LAP lapped, not placed (UCI) — a lapped rider who IS placed has no mark,
 *       only `laps_delta` and a place        OTL outside the time limit (UCI)
 *   REL relegated (BMX)                      CUT missed the cut (golf)
 *   EL  eliminated (FEI)                     FTQ failed to qualify (FEI endurance vet gate)
 *   PEN penalised (ISU short track)
 */
export const IRM_CODES = [
	"DNS",
	"DNF",
	"DSQ",
	"NM",
	"WD",
	"WO",
	"RET",
	"DNC",
	"OCS",
	"BFD",
	"UFD",
	"DNE",
	"RDG",
	"DPI",
	"SCP",
	"ZFP",
	"STP",
	"DCT",
	"NSC",
	"LAP",
	"OTL",
	"REL",
	"CUT",
	"EL",
	"FTQ",
	"PEN",
] as const;
export type IrmCode = (typeof IRM_CODES)[number];

/**
 * How a competitor got through to the next phase — a DECISION, stored because
 * it cannot be recomputed: by finishing place ("Q"), by performance among the
 * rest ("q"), by a referee's or jury's decision, or as a reserve.
 */
export const QUALIFICATION_MARKS = ["BY_PLACE", "BY_PERFORMANCE", "BY_DECISION", "RESERVE"] as const;
export type QualificationMark = (typeof QUALIFICATION_MARKS)[number];

/**
 * People at a meet who do not compete. They are rows in `meet_officials`,
 * never in the competitor list: the predecessor kept 1,925 judges, coaches and
 * managers among its participants, and a final once listed six coaches as
 * medal candidates.
 */
export const OFFICIAL_ROLES = [
	"REFEREE",
	"UMPIRE",
	"JUDGE",
	"STARTER",
	"TIMEKEEPER",
	"TECHNICAL_DELEGATE",
	"COACH",
	"MANAGER",
	"OTHER",
] as const;
export type OfficialRole = (typeof OFFICIAL_ROLES)[number];

// ─── Formats ─────────────────────────────────────────────────────────────────
//
// Same reasoning as the lists above, for the four fields whose FORMAT the
// database enforces. Each pattern is what the STORED value must match: the
// CHECK on the column is generated from it (the schema reads `.source`), and
// the zod schema for the field should test against this same constant — after
// upper-casing, where a form accepts lower case, as `makeFunctionCodeField`
// does — so the form and the database cannot disagree about what a valid code
// is.
//
// Keep them to syntax JavaScript and Postgres read identically — anchors,
// character classes, `{m,n}` — with no flags, no backslash and no quote. The
// unit test refuses anything else.

/** `games.code` — SEAG, APG. */
export const GAMES_CODE_PATTERN = /^[A-Z0-9]{2,10}$/;

/** `code` on sports, disciplines, events and venues — AQ, SWM, M100FR, NAC-C1. */
export const COMPETITION_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{0,19}$/;

/** A pool / group label on a meet or a phase entry — A, B, 1, A2. */
export const POOL_PATTERN = /^[A-Z0-9]{1,4}$/;

/** Which side of a head-to-head a slot takes — HOME, AWAY, RED, BLUE. */
export const SIDE_PATTERN = /^[A-Z0-9_]{1,12}$/;

/**
 * What a discipline calls the two sides of a head-to-head — `disciplines.side_scheme`.
 *
 *   SEQUENCE     no named sides: start order 1 is the top (first) slot, 2 the
 *                bottom. Sepak takraw, badminton, tennis, volleyball — the
 *                default, since most net and racket sports have no home team.
 *   HOME_AWAY    football and the other invasion games.
 *   RED_BLUE     taekwondo, boxing, pencak silat, wushu, muay.
 *   WHITE_BLUE   judo (the first called wears white).
 *   AKA_AO       karate (aka = red, ao = blue).
 *
 * It decides what OVR shows and what a new slot's `side` is set to; `side`
 * itself stays free text (`SIDE_PATTERN`), so a row stored before a scheme
 * changed is still readable.
 */
export const SIDE_SCHEMES = ["SEQUENCE", "HOME_AWAY", "RED_BLUE", "WHITE_BLUE", "AKA_AO"] as const;
export type SideScheme = (typeof SIDE_SCHEMES)[number];

/** A named scheme's side codes, first (top) then second; `null` for SEQUENCE (the start order decides). */
export const SIDE_SCHEME_SIDES = {
	SEQUENCE: null,
	HOME_AWAY: ["HOME", "AWAY"],
	RED_BLUE: ["RED", "BLUE"],
	WHITE_BLUE: ["WHITE", "BLUE"],
	AKA_AO: ["AKA", "AO"],
} as const satisfies Record<SideScheme, readonly [string, string] | null>;

/** Every side code a named scheme uses — HOME, AWAY, RED, BLUE, WHITE, AKA, AO. */
export const SCHEME_SIDE_CODES = ["HOME", "AWAY", "RED", "BLUE", "WHITE", "AKA", "AO"] as const;
export type SchemeSideCode = (typeof SCHEME_SIDE_CODES)[number];
